import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";

/**
 * v1.3.0 — Admin page: connect master Gmail + manage routing rules.
 */
export default function EmailTool() {
  const [status, setStatus] = useState({ connected: false, configured: false, supports_app_password: true });
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState("");
  const [connectMode, setConnectMode] = useState("app_password");  // v1.3.1: prefer simpler path
  const [appCreds, setAppCreds] = useState({ email: "", app_password: "" });
  const [connecting, setConnecting] = useState(false);
  const [form, setForm] = useState({
    label: "",
    from_contains: "",
    subject_contains: "",
    body_contains: "",
    assigned_user_id: "",
  });

  async function refresh() {
    setErr("");
    try {
      const [s, r, u] = await Promise.all([
        api.get("/email/oauth/status"),
        api.get("/email/rules"),
        api.get("/admin/users"),
      ]);
      setStatus(s.data);
      setRules(r.data || []);
      // Only users with email_tool_enabled can be assigned
      const candidates = (u.data || []).filter(
        (x) => x.active && (x.policy || {}).email_tool_enabled
      );
      setUsers(candidates);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function connect() {
    setErr("");
    try {
      const r = await api.get("/email/oauth/start");
      window.open(r.data.url, "_blank", "width=520,height=640");
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  }

  async function connectAppPassword() {
    if (!appCreds.email.trim() || !appCreds.app_password.trim()) {
      setErr("Email and App Password are both required");
      return;
    }
    setConnecting(true);
    setErr("");
    try {
      await api.post("/email/connect-app-password", appCreds);
      setAppCreds({ email: "", app_password: "" });
      refresh();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setConnecting(false);
    }
  }

  async function disconnect() {
    if (!window.confirm("Disconnect master mailbox? Polling will stop.")) return;
    await api.delete("/email/oauth/disconnect");
    refresh();
  }

  async function pollNow() {
    setErr("");
    try {
      const r = await api.post("/email/poll-now");
      alert(
        `Poll result: ${JSON.stringify(r.data)}\nNew: ${r.data.new || 0}  Assigned: ${
          r.data.assigned || 0
        }  Skipped: ${r.data.skipped || 0}`
      );
      refresh();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  }

  async function addRule() {
    if (!form.label.trim() || !form.assigned_user_id) {
      setErr("Label and Assigned user are required");
      return;
    }
    setErr("");
    try {
      await api.post("/email/rules", form);
      setForm({
        label: "",
        from_contains: "",
        subject_contains: "",
        body_contains: "",
        assigned_user_id: "",
      });
      refresh();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  }

  async function deleteRule(id) {
    if (!window.confirm("Delete this rule?")) return;
    await api.delete(`/email/rules/${id}`);
    refresh();
  }

  async function toggleRule(r) {
    await api.patch(`/email/rules/${r.id}`, { enabled: !r.enabled });
    refresh();
  }

  return (
    <div className="p-10 max-w-6xl">
      <h1 className="text-2xl font-semibold mb-1">Email Tool</h1>
      <p className="text-zinc-500 text-sm mb-6">
        Connect a single Google Workspace mailbox and route incoming emails
        to designated agents.
      </p>

      {err && (
        <div
          data-testid="email-tool-error"
          className="mb-4 p-3 rounded border border-rose-500/40 bg-rose-500/10 text-rose-300 text-sm"
        >
          {err}
        </div>
      )}

      {/* Connection status */}
      <section
        data-testid="email-conn-card"
        className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-5 mb-6"
      >
        {status.connected ? (
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm text-zinc-400">Master mailbox</div>
              <div className="text-lg font-medium" data-testid="email-conn-status">
                Connected: <span className="text-emerald-400">{status.email}</span>
                <span className="ml-3 text-xs px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 uppercase tracking-wide">
                  {status.auth_mode === "imap" ? "App password" : "Google OAuth"}
                </span>
              </div>
              {status.last_polled_at && (
                <div className="text-xs text-zinc-500 mt-1">
                  Last polled: {new Date(status.last_polled_at).toLocaleString()}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                data-testid="email-poll-now-btn"
                onClick={pollNow}
                className="px-3 py-2 text-sm rounded-md border border-zinc-700 hover:bg-zinc-900"
              >
                Poll Now
              </button>
              <button
                data-testid="email-disconnect-btn"
                onClick={disconnect}
                className="px-3 py-2 text-sm rounded-md border border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
              >
                Disconnect
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-sm text-zinc-400 mb-3">Connect a master mailbox</div>
            <div data-testid="email-conn-status" className="hidden">Not connected</div>
            {/* Tabs */}
            <div className="flex gap-2 mb-4 border-b border-zinc-800">
              <button
                data-testid="conn-tab-app-password"
                onClick={() => setConnectMode("app_password")}
                className={`px-3 py-2 text-sm -mb-px border-b-2 ${
                  connectMode === "app_password"
                    ? "border-emerald-400 text-emerald-300"
                    : "border-transparent text-zinc-500 hover:text-zinc-300"
                }`}
              >
                App Password (recommended)
              </button>
              <button
                data-testid="conn-tab-oauth"
                onClick={() => setConnectMode("oauth")}
                className={`px-3 py-2 text-sm -mb-px border-b-2 ${
                  connectMode === "oauth"
                    ? "border-emerald-400 text-emerald-300"
                    : "border-transparent text-zinc-500 hover:text-zinc-300"
                }`}
              >
                Google OAuth
              </button>
            </div>

            {connectMode === "app_password" ? (
              <div>
                <ol className="text-xs text-zinc-400 list-decimal ml-4 space-y-1 mb-4">
                  <li>Open <a className="text-emerald-400 underline" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">myaccount.google.com/apppasswords</a> while signed in as the master mailbox (e.g. seller@upcpl.com).</li>
                  <li>Generate a new App Password (label it "SND Secure Browser"). 2-Step Verification must be on.</li>
                  <li>Copy the 16-character password and paste it below. We store it encrypted with the same key as your password vault.</li>
                </ol>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    data-testid="conn-app-email"
                    type="email"
                    value={appCreds.email}
                    onChange={(e) => setAppCreds({ ...appCreds, email: e.target.value })}
                    placeholder="Mailbox address (seller@upcpl.com)"
                    className="px-3 py-2 rounded bg-zinc-900 border border-zinc-800 text-sm"
                  />
                  <input
                    data-testid="conn-app-password"
                    type="password"
                    value={appCreds.app_password}
                    onChange={(e) => setAppCreds({ ...appCreds, app_password: e.target.value })}
                    placeholder="16-character app password"
                    className="px-3 py-2 rounded bg-zinc-900 border border-zinc-800 text-sm font-mono"
                    autoComplete="off"
                  />
                </div>
                <button
                  data-testid="conn-app-submit"
                  disabled={connecting}
                  onClick={connectAppPassword}
                  className="mt-4 px-4 py-2 text-sm rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-medium disabled:opacity-40"
                >
                  {connecting ? "Connecting…" : "Connect mailbox"}
                </button>
              </div>
            ) : (
              <div>
                {!status.configured ? (
                  <div className="text-xs text-amber-400 mb-3">
                    OAuth not configured. Set GOOGLE_CLIENT_ID + GOOGLE_CLIENT_SECRET in
                    the backend env, or use the App Password tab instead.
                  </div>
                ) : (
                  <p className="text-xs text-zinc-400 mb-3">
                    Sign in as the workspace mailbox owner. We store only a refresh
                    token (no password).
                  </p>
                )}
                <button
                  data-testid="email-connect-btn"
                  disabled={!status.configured}
                  onClick={connect}
                  className="px-4 py-2 text-sm rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Connect Gmail (OAuth)
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Add rule form */}
      <section className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-5 mb-6">
        <h2 className="text-base font-medium mb-3">Add routing rule</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            data-testid="rule-label-input"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="Rule label (e.g. Returns)"
            className="px-3 py-2 rounded bg-zinc-900 border border-zinc-800 text-sm"
          />
          <select
            data-testid="rule-assignee-select"
            value={form.assigned_user_id}
            onChange={(e) => setForm({ ...form, assigned_user_id: e.target.value })}
            className="px-3 py-2 rounded bg-zinc-900 border border-zinc-800 text-sm"
          >
            <option value="">Assign to user…</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.email}</option>
            ))}
          </select>
          <input
            data-testid="rule-from-input"
            value={form.from_contains}
            onChange={(e) => setForm({ ...form, from_contains: e.target.value })}
            placeholder="From contains (e.g. @amazon.in)"
            className="px-3 py-2 rounded bg-zinc-900 border border-zinc-800 text-sm"
          />
          <input
            data-testid="rule-subject-input"
            value={form.subject_contains}
            onChange={(e) => setForm({ ...form, subject_contains: e.target.value })}
            placeholder="Subject contains"
            className="px-3 py-2 rounded bg-zinc-900 border border-zinc-800 text-sm"
          />
          <input
            data-testid="rule-body-input"
            value={form.body_contains}
            onChange={(e) => setForm({ ...form, body_contains: e.target.value })}
            placeholder="Body contains (keyword)"
            className="px-3 py-2 rounded bg-zinc-900 border border-zinc-800 text-sm md:col-span-2"
          />
        </div>
        {users.length === 0 && (
          <div className="mt-3 text-xs text-amber-400">
            No users have Email Tool access yet. Enable it on a user's policy first
            (Users → click a user → toggle "Email Tool access").
          </div>
        )}
        <button
          data-testid="rule-add-btn"
          onClick={addRule}
          className="mt-4 px-4 py-2 text-sm rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-medium"
        >
          Add rule
        </button>
      </section>

      {/* Rules table */}
      <section className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-medium">Routing rules</h2>
          <div className="text-xs text-zinc-500">First match wins (top → bottom)</div>
        </div>
        {rules.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6">No rules yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="email-rules-table">
            <thead>
              <tr className="text-left text-zinc-500">
                <th className="py-2 pr-3">#</th>
                <th className="py-2 pr-3">Label</th>
                <th className="py-2 pr-3">Match</th>
                <th className="py-2 pr-3">Assignee</th>
                <th className="py-2 pr-3">Enabled</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r, i) => {
                const u = users.find((x) => x.id === r.assigned_user_id);
                const matchParts = [];
                if (r.from_contains) matchParts.push(`from~"${r.from_contains}"`);
                if (r.to_contains) matchParts.push(`to~"${r.to_contains}"`);
                if (r.subject_contains) matchParts.push(`subject~"${r.subject_contains}"`);
                if (r.body_contains) matchParts.push(`body~"${r.body_contains}"`);
                return (
                  <tr key={r.id} className="border-t border-zinc-800/60">
                    <td className="py-2 pr-3 text-zinc-500">{i + 1}</td>
                    <td className="py-2 pr-3 font-medium">{r.label}</td>
                    <td className="py-2 pr-3 text-zinc-400 text-xs">
                      {matchParts.length ? matchParts.join(" AND ") : <span className="italic text-amber-500">catch-all</span>}
                    </td>
                    <td className="py-2 pr-3 text-zinc-300">{u?.email || <span className="text-rose-400 text-xs">user disabled</span>}</td>
                    <td className="py-2 pr-3">
                      <button
                        data-testid={`rule-toggle-${r.id}`}
                        onClick={() => toggleRule(r)}
                        className={`px-2 py-1 rounded text-xs ${
                          r.enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-800 text-zinc-500"
                        }`}
                      >
                        {r.enabled ? "Enabled" : "Disabled"}
                      </button>
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <button
                        data-testid={`rule-delete-${r.id}`}
                        onClick={() => deleteRule(r.id)}
                        className="text-xs text-rose-400 hover:text-rose-300"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
